<script setup>
import { ref, computed, watch, nextTick } from 'vue';
import { OnClickOutside } from '@vueuse/components';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';

const props = defineProps({
  type: {
    type: String,
    default: 'edit',
    validator: value => ['alert', 'edit'].includes(value),
  },
  title: {
    type: String,
    default: '',
  },
  description: {
    type: String,
    default: '',
  },
  cancelButtonLabel: {
    type: String,
    default: '',
  },
  confirmButtonLabel: {
    type: String,
    default: '',
  },
  disableConfirmButton: {
    type: Boolean,
    default: false,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  showCancelButton: {
    type: Boolean,
    default: true,
  },
  showConfirmButton: {
    type: Boolean,
    default: true,
  },
  overflowYAuto: {
    type: Boolean,
    default: false,
  },
  width: {
    type: String,
    default: 'lg',
    validator: value =>
      [
        '7xl',
        '6xl',
        '5xl',
        '4xl',
        '3xl',
        '2xl',
        'xl',
        'lg',
        'md',
        'sm',
      ].includes(value),
  },
});

const emit = defineEmits(['confirm', 'close']);

const { t } = useI18n();

const isOpen = ref(false);
const dialogContentRef = ref(null);
// `aria-modal` sem nome, sem foco e sem trap era só promessa: o leitor de tela
// não anunciava o diálogo e o Tab continuava passeando pela página atrás do
// backdrop — inclusive em confirmação de coisa cara. Ver
// .impeccable/critique/2026-09-19T21-39-07Z__ponents-next-conversation-webchatreplycontrols-vue.md
const titleId = `dialog-title-${Math.random().toString(36).slice(2, 9)}`;
const previouslyFocused = ref(null);

const focusableInDialog = () => [
  ...(dialogContentRef.value?.querySelectorAll(
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
  ) ?? []),
];

// Tab circula dentro do diálogo enquanto ele está aberto.
const trapTab = e => {
  if (e.key !== 'Tab' || !isOpen.value) return;
  const focusables = focusableInDialog();
  if (!focusables.length) return;
  const first = focusables[0];
  const last = focusables[focusables.length - 1];
  const active = document.activeElement;
  if (
    e.shiftKey &&
    (active === first || !dialogContentRef.value?.contains(active))
  ) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && active === last) {
    e.preventDefault();
    first.focus();
  }
};

const maxWidthStyle = computed(() => {
  const widthMap = {
    '7xl': '80rem',
    '6xl': '72rem',
    '5xl': '64rem',
    '4xl': '56rem',
    '3xl': '48rem',
    '2xl': '42rem',
    xl: '36rem',
    lg: '32rem',
    md: '28rem',
    sm: '24rem',
  };

  return {
    maxWidth: widthMap[props.width] || '32rem',
    width: '100%',
  };
});

const open = async () => {
  previouslyFocused.value =
    document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
  isOpen.value = true;
  document.body.style.overflow = 'hidden';
  // Aguarda o próximo tick para garantir que o conteúdo foi renderizado
  await nextTick();
  // Foco no PRIMEIRO controle do diálogo (Cancelar, quando existe): a ação
  // padrão de uma confirmação não pode ser a destrutiva/cara.
  focusableInDialog()[0]?.focus();
};

const close = () => {
  isOpen.value = false;
  document.body.style.overflow = '';
  // Devolve o foco a quem abriu; senão ele cai no <body> e o teclado recomeça
  // do topo da página.
  previouslyFocused.value?.focus?.();
  previouslyFocused.value = null;
  emit('close');
};

const confirm = () => {
  emit('confirm');
};

const handleEscape = e => {
  if (e.key === 'Escape' && isOpen.value) {
    close();
  }
};

watch(isOpen, newValue => {
  if (newValue) {
    document.addEventListener('keydown', handleEscape);
    document.addEventListener('keydown', trapTab, true);
  } else {
    document.removeEventListener('keydown', handleEscape);
    document.removeEventListener('keydown', trapTab, true);
  }
});

defineExpose({ open, close });
</script>

<template>
  <TeleportWithDirection to="body">
    <Transition
      enter-active-class="transition duration-300 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition duration-200 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="isOpen"
        class="fixed inset-0 z-[9999] flex items-center justify-center"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="title ? titleId : undefined"
        :aria-label="title ? undefined : t('DIALOG.BUTTONS.CONFIRM')"
      >
        <div
          class="fixed inset-0 bg-n-alpha-black1 backdrop-blur-[4px]"
          aria-hidden="true"
          @click="close"
        />

        <div class="fixed inset-0 w-screen overflow-y-auto p-4">
          <div class="flex min-h-full items-center justify-center p-4">
            <OnClickOutside
              :options="{
                ignore: [
                  '.filter-select-dropdown',
                  '[data-filter-dropdown]',
                  '.multiselect-dropdown',
                  '[data-multiselect-dropdown]',
                  '.singleselect-dropdown',
                  '[data-singleselect-dropdown]',
                  // vue-datepicker-next usa `append-to-body`: o calendário vive
                  // no <body>, fora do diálogo. Sem isto, escolher uma data
                  // conta como clique fora e fecha o diálogo por baixo.
                  '.mx-datepicker-main',
                ],
              }"
              @trigger="close"
            >
              <div
                class="relative z-[10000] transition-all duration-300 ease-in-out shadow-xl rounded-xl"
                :style="maxWidthStyle"
              >
                <form
                  ref="dialogContentRef"
                  class="flex flex-col w-full h-auto gap-6 p-6 overflow-visible text-left align-middle transform bg-n-alpha-3 backdrop-blur-[100px] shadow-xl rounded-xl"
                  @submit.prevent="confirm"
                  @click.stop
                >
                  <div v-if="title || description" class="flex flex-col gap-2">
                    <h3
                      :id="titleId"
                      class="text-base font-medium leading-6 text-n-slate-12"
                    >
                      {{ title }}
                    </h3>
                    <slot name="description">
                      <p
                        v-if="description"
                        class="mb-0 text-sm text-n-slate-11"
                      >
                        {{ description }}
                      </p>
                    </slot>
                  </div>
                  <slot />
                  <slot name="footer">
                    <div
                      v-if="showCancelButton || showConfirmButton"
                      class="flex items-center justify-between w-full gap-3"
                    >
                      <Button
                        v-if="showCancelButton"
                        variant="faded"
                        color="slate"
                        :label="cancelButtonLabel || t('DIALOG.BUTTONS.CANCEL')"
                        class="w-full"
                        type="button"
                        @click="close"
                      />
                      <Button
                        v-if="showConfirmButton"
                        :color="type === 'edit' ? 'blue' : 'ruby'"
                        :label="
                          confirmButtonLabel || t('DIALOG.BUTTONS.CONFIRM')
                        "
                        class="w-full"
                        :is-loading="isLoading"
                        :disabled="disableConfirmButton || isLoading"
                        type="submit"
                      />
                    </div>
                  </slot>
                </form>
              </div>
            </OnClickOutside>
          </div>
        </div>
      </div>
    </Transition>
  </TeleportWithDirection>
</template>
